'use strict';
import MultiPickerController from './mx-multi-picker.controller';
import template from './mx-multi-picker.html';
import { commonPickerProperties } from './mx-picker-properties';

/**
 * @ngdoc directive
 * @name mx.components:mxMultiPicker
 * @module mx.components
 * @restrict 'E'
 * @description
 * The mx-multi-picker control is used to create a picker where the user can select multiple items.
 *
 * The example below demonstrates some of the attributes you may use with the MultiPicker control:
 * @param {string} name@ - The name property sets or returns the value of the name attribute.
 * @param {string} label@ - Defines control label displayed on the form.
 * @param {boolean} required= - The required property sets or returns whether a mxMultiPicker must be filled out before submitting a form.
 * @param {boolean} disabled= - The disabled property sets or returns whether a mxMultiPicker should be disabled, or not.
 * @param {boolean} readOnly= - The readOnly property sets or returns whether the contents of a mxPicker should be read-only.
 * @param {object} model=ngModel - directive binds element to the object in the model.
 * @param {object[]} items= - The items property sets or returns items to be loaded for selection.
 * @param {string} itemTitleField@ - Specify field name to be displayed as picker item name. Default `title`.
 * @param {string} itemIdField@ - field name to be interpreted as ID. Default `id`.
 * @param {string} itemDropdownLabelField@ - Specify field name to be displayed as picker dropdown item label. If not set then `itemTitleField` will be used.
 * @param {function(searchText:string): object} loadItems= - Callback function for loading
 * available `items` which match the typed text. Returns the object has the following properties:
 *
 * - `items` – `{Array}` – Array of Items.
 * - `searchText` – `{string}` – Keyword text for which `items` been found
 * - `all` – `{boolean}` – Signals whether the all `items` been returns which fits the `searchText`.
 * @param {boolean} loadOnTyping= - Reloads items on typing.
 * @param {int} loadDelay= - Delay (ms) before data loading start
 * @param {function(item:object)} navigateItem= - A callback Function which handles the selected Item navigation event. If the property is not defined
 * the navigation logic is disabled.
 * The function accepts the following parameters:
 * - `item` – `{object}` – Item to navigate.
 *
 * @param {function(items:object[]):object[]} browseLookup= - A callback function which provides extended view for
 * browsing available items. Returns an array of extra selected item. In case `null` is returned, then the control selection is clean up.
 * If function returns `undefined` then the operation skipped.
 * Parameters:
 * - `items` – `{object[]}` – Array of currently selected items. `null` if nothing selected.
 *
 * @param {string} dropdownHtmlClass@ - This will be applied to the dropdown menu for styling
 * @param {function} onAdd - a function which will be executed when item will be added to chips-list. Gets `$chip` and `$index` as a parameters.
 * @param {function} onRemove - a function which will be executed when item will be removed from chips-list. Gets `$chip` and `$index` as a parameters.
 * @param {function} dropdownLabelsDecorator - a function for custom decorating dropdown labels.
 * 		Gets `label` and `searchText` as parameters and should return decorated `label` as html.
 *
 * @usage
 *	<mx-multi-picker
 *		class="mx-multi-picker-default"
 *		items="vm.pickerData"
 *		ng-model="vm.pickerValue"
 *		ng-required="true"
 *		data-label="Classic, predfined items"
 *		data-item-title-field="title">
 *	</mx-multi-picker>
 *
 */
angular.module('mx.components').directive('mxMultiPicker', function mxMultiPickerDirective() {
	const directive = new mx.components.FormControlBase(MultiPickerController, null, template);
	angular.extend(directive.bindToController, commonPickerProperties);
	angular.extend(directive.bindToController, {
		separatorChar: '@',
		onAdd: '&',
		onRemove: '&',
		dropdownLabelsDecorator: '<',
		removable: '<',
		addOnBlur: '<?',
	});
	return directive;
});
